/**
 * new-bot-wizard-page.tsx — Luồng tạo nhân viên AI 5 bước (`/ai/nhan-vien/moi`, toàn trang) theo khung ba tầng
 * (docs/KE-HOACH-KHUNG-AI-3-TANG.md §6): 1 Chọn mẫu & tên → 2 Dạy kiến thức (bỏ qua được) → 3 Chạy thử → 4 Kênh & giờ làm
 * → 5 Ra mắt. Bước 1 tạo bản nháp ngay (`POST /ai/bots`, tắt, chưa giao kênh); từ đó khung chat thử hiện bên phải.
 *
 * Trạng thái trên URL: `?bot=<id>&buoc=<1..5>&mau=<mẫu>` — tải lại / rời trang rồi vào lại (từ danh sách hoặc trang Bắt
 * đầu) vẫn tiếp tục đúng bản nháp. Lượt chạy thử + bước xa nhất đã tới lưu ở sessionStorage của trình duyệt (theo bot).
 * Thanh bước phản ánh dữ liệu thật (wizard-lib.stepStates), cho quay lại bước trước.
 * Chuyển bước = cùng tab; sang màn khác (cài đặt chi tiết, Kho tri thức, Kết nối, Hộp thư…) = TAB MỚI, luồng tạo giữ nguyên.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Check, MessageSquareText, SkipForward } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { BotTestChat, type BotTestChatHandle, type ChatTurn } from '@/components/ai/bot-test-chat'
import { OpenInNewTab, openInNewTab } from '@/components/ai/open-in-new-tab'
import { cn } from '@/lib/utils'
import { useKnowledgeBases } from '@/hooks/use-knowledge-bases'
import { AddAiMemberDialog } from '@/pages/team/add-ai-member-dialog'
import { presetModelFromParams } from '@/pages/team/byok-start-lib'
import { useBotStatus, useInvalidateBot, useWizardBot, type WizardBot } from './wizard-api'
import {
  WIZARD_STEPS, botDetailPath, botKnowledgeBaseIds, dislikeHints, knowledgeReady, stepFromParam, stepStates,
  type StepState,
} from './wizard-lib'
import { templateByKey, type WizardTemplateKey } from './wizard-templates'
import { StepTemplate } from './step-template'
import { StepKnowledge } from './step-knowledge'
import { StepTest } from './step-test'
import { StepChannels } from './step-channels'
import { StepLaunch } from './step-launch'
import { readMemo, readTurns, writeMemo, writeTurns, type WizardMemo } from './wizard-storage'
import { useAiPerms } from '../use-ai-perms'

export function NewBotWizardPage() {
  const { t } = useTranslation('aiWizard')
  // Tạo / sửa / chạy thử nhân viên AI: backend đòi `ai.update` (ai-bot-routes, simulate-routes).
  const { canEdit, loaded: permsLoaded } = useAiPerms()
  const [params, setParams] = useSearchParams()
  const botId = params.get('bot')
  // Lối "Tạo bằng Key của tôi" (byok-start-dialog) → ?provider=&model= : bot mới tạo chạy đúng model đó (key của khách).
  const presetModel = botId ? null : presetModelFromParams(params)
  // Không có ?buoc= (vào lại từ danh sách / trang Bắt đầu) → bước xa nhất đã tới trên trình duyệt này.
  const step = stepFromParam(params.get('buoc') ?? (botId ? String(readMemo(botId)?.maxVisited ?? '') : null), !!botId)
  const botQ = useWizardBot(botId)
  const bot = botQ.data ?? null
  const statusQ = useBotStatus(botId, !!bot)
  const { data: bases } = useKnowledgeBases()
  const invalidate = useInvalidateBot()
  const chatRef = useRef<BotTestChatHandle>(null)
  const [externalOpen, setExternalOpen] = useState(false)

  const memo: WizardMemo = (botId ? readMemo(botId) : null) ?? { maxVisited: step, template: null }
  const templateKey = (templateByKey(params.get('mau'))?.key ?? memo.template) as WizardTemplateKey | null
  const template = templateByKey(templateKey) ?? templateByKey('trong')!

  // Lượt chạy thử — giữ theo bot trong phiên trình duyệt.
  const [turns, setTurns] = useState<ChatTurn[]>([])
  useEffect(() => { setTurns(botId ? readTurns(botId) : []) }, [botId])
  const updateTurns = useCallback((next: ChatTurn[] | ((prev: ChatTurn[]) => ChatTurn[])) => {
    setTurns((prev) => {
      const v = typeof next === 'function' ? next(prev) : next
      if (botId) writeTurns(botId, v)
      return v
    })
  }, [botId])

  // Ghi bước xa nhất + mẫu.
  useEffect(() => {
    if (!botId) return
    const m = readMemo(botId) ?? { maxVisited: 1, template: null }
    writeMemo(botId, { maxVisited: Math.max(m.maxVisited, step), template: templateKey ?? m.template })
  }, [botId, step, templateKey])

  const goStep = useCallback((n: number, extra?: Record<string, string>) => {
    setParams((p) => {
      const next = new URLSearchParams(p)
      next.set('buoc', String(n))
      for (const [k, v] of Object.entries(extra ?? {})) next.set(k, v)
      return next
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [setParams])

  const refreshBot = useCallback(() => invalidate(botId), [invalidate, botId])

  if (!permsLoaded) return <Loading label={t('page.checkingPerms')} />
  if (!canEdit) {
    return (
      <div className="space-y-4">
        <PageHeader title={t('page.title')} />
        <ErrorState message={t('page.noPerm')} />
      </div>
    )
  }
  if (botId && botQ.isLoading) return <Loading label={t('page.openingDraft')} />
  if (botId && botQ.isError) {
    return (
      <div className="space-y-4">
        <PageHeader title={t('page.title')} />
        <ErrorState message={t('page.draftError')} />
        <Link to="/ai/nhan-vien/moi" className="text-sm text-primary hover:underline">{t('page.startNew')}</Link>
      </div>
    )
  }

  const states = stepStates({
    hasBot: !!bot,
    knowledgeReady: knowledgeReady(bot, bases),
    testTurns: turns.filter((x) => !x.error).length,
    channelCount: bot?.channelAccountIds?.length ?? 0,
    live: !!bot?.enabled && !!statusQ.data?.answering,
    maxVisited: Math.max(memo.maxVisited, step),
  })

  const ask = (text: string) => {
    if (step === 1) return
    chatRef.current?.send(text)
  }

  function onCreated(saved: WizardBot, key: WizardTemplateKey) {
    writeMemo(saved.id, { maxVisited: 2, template: key })
    setParams({ bot: saved.id, buoc: '2', mau: key })
    void invalidate(saved.id)
  }

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Link to="/ai/nhan-vien" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-3.5 w-3.5" /> {t('page.back')}
        </Link>
        <PageHeader
          title={bot ? t('page.titleBot', { emoji: bot.avatarEmoji ?? '', name: bot.name }).trim() : t('page.title')}
          description={bot ? t('page.descDraft') : t('page.descNew')}
          actions={bot ? <OpenInNewTab to={botDetailPath(bot.id)} className="text-xs" title={t('page.openDetailTitle')}>{t('page.openDetail')}</OpenInNewTab> : undefined}
        />
      </div>

      <StepBar current={step} states={states} canJump={!!bot} onJump={(n) => goStep(n)} />

      <div className={cn(bot && 'grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]')}>
        <div className="min-w-0">
          {step === 1 && (
            <StepTemplate bot={bot} templateKey={templateKey} presetModel={presetModel} onOpenExternal={() => setExternalOpen(true)}
              onSaved={(saved, key) => {
                if (!bot) { onCreated(saved, key); return }
                void refreshBot()
                goStep(2, { mau: key })
              }} />
          )}
          {bot && step === 2 && (
            <StepKnowledge bot={bot} onBotChange={refreshBot} onAsk={ask} onBack={() => goStep(1)} onNext={() => goStep(3)} />
          )}
          {bot && step === 3 && (
            <StepTest botId={bot.id} botName={bot.name} template={template} turns={turns} onAsk={ask}
              onGoKnowledge={() => goStep(2)} onBack={() => goStep(2)} onNext={() => goStep(4)} />
          )}
          {bot && step === 4 && (
            <StepChannels key={bot.updatedAt} bot={bot} onBack={() => goStep(3)}
              onSaved={async () => { await refreshBot(); goStep(5) }} />
          )}
          {bot && step === 5 && (
            <StepLaunch bot={bot} onChanged={refreshBot} onGoStep={(n) => goStep(n)} onBack={() => goStep(4)} />
          )}
        </div>

        {bot && (
          <aside className="flex h-[560px] flex-col rounded-xl border bg-card p-3 lg:sticky lg:top-4 lg:h-[calc(100vh-7rem)]" aria-label={t('page.chatAria')}>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium">
              <MessageSquareText className="h-4 w-4 text-primary" /> {t('page.chatWith', { emoji: bot.avatarEmoji ?? '🤖', name: bot.name })}
            </p>
            <BotTestChat
              ref={chatRef}
              className="min-h-0 flex-1"
              botId={bot.id} botName={bot.name} botEmoji={bot.avatarEmoji ?? '🤖'}
              turns={turns} onTurnsChange={updateTurns}
              knowledgeBaseId={botKnowledgeBaseIds(bot)[0] ?? null}
              hintsFor={(turn) => dislikeHints({ sourceCount: turn.sources.length }).map((h) => ({
                key: h.key, label: h.label, desc: h.desc,
                onClick: () => (h.key === 'kien-thuc' ? goStep(2) : openInNewTab(botDetailPath(bot.id, 'vai-tro'))),
              }))}
            />
          </aside>
        )}
      </div>

      <AddAiMemberDialog open={externalOpen} onOpenChange={setExternalOpen} canEdit={canEdit} />
    </div>
  )
}

function StepBar({ current, states, canJump, onJump }: {
  current: number
  states: Record<(typeof WIZARD_STEPS)[number]['key'], StepState>
  canJump: boolean
  onJump: (n: number) => void
}) {
  const { t } = useTranslation('aiWizard')
  return (
    <nav aria-label={t('page.stepsAria')}>
      <ol className="flex flex-wrap gap-1.5 sm:flex-nowrap">
        {WIZARD_STEPS.map((s) => {
          const st = states[s.key]
          const active = s.n === current
          const clickable = canJump && !active
          return (
            <li key={s.key} className="min-w-[8.5rem] flex-1">
              <button
                type="button" disabled={!clickable} onClick={() => onJump(s.n)} aria-current={active ? 'step' : undefined}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-colors',
                  active ? 'border-primary bg-primary/5 font-medium text-primary' : 'text-muted-foreground',
                  clickable && 'hover:border-primary/50 hover:text-foreground',
                  !clickable && !active && 'cursor-default',
                )}
              >
                <span className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px]',
                  st === 'done' && 'border-success bg-success text-white',
                  st === 'skipped' && 'border-muted-foreground/40',
                  active && st !== 'done' && 'border-primary',
                )}>
                  {st === 'done' ? <Check className="h-3 w-3" /> : st === 'skipped' ? <SkipForward className="h-3 w-3" /> : s.n}
                </span>
                <span className="min-w-0">
                  <span className="block truncate">{s.title}</span>
                  <span className="block text-[10px] font-normal text-muted-foreground">
                    {st === 'done' ? t('page.stepState.done') : st === 'skipped' ? t('page.stepState.skipped') : s.optional ? t('page.stepState.optional') : t('page.stepState.todo')}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
